import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './PendingReviewBar.css';

async function fetchPending(token) {
  const params = new URLSearchParams({
    review_state: 'pending',
    sort_on: 'modified',
    sort_order: 'descending',
    b_size: '50',
  });
  ['modified', 'Creator', 'portal_type'].forEach((f) =>
    params.append('metadata_fields', f),
  );
  const res = await fetch(`/++api++/@search?${params}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function toApiUrl(frontendUrl) {
  // Wandelt  https://www.sv-reudnitz.de/teams/002/20260918
  // um in    /++api++/teams/002/20260918
  const url = new URL(frontendUrl);
  return `/++api++${url.pathname}`;
}

async function doTransition(url, transition, token) {
  const apiUrl = toApiUrl(url);   // <-- neu
  const res = await fetch(`${apiUrl}/@workflow/${transition}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ comment: '' }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function PendingReviewBar() {
  const token      = useSelector((state) => state.userSession?.token);
  const isLoggedIn = Boolean(token);

  const [items, setItems]   = useState([]);
  const [busy, setBusy]     = useState(null);

  const load = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const data = await fetchPending(token);
      setItems(data.items ?? []);
    } catch (_) {}
  }, [token, isLoggedIn]);

  useEffect(() => {
    load();
  }, [load]);

  // Nicht eingeloggt oder nichts freizugeben → gar nichts rendern
  if (!isLoggedIn || items.length === 0) return null;

  async function handleAction(item, transition) {
    setBusy(item['@id']);
    try {
      await doTransition(item['@id'], transition, token);
      setItems((prev) => prev.filter((i) => i['@id'] !== item['@id']));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="prb-container">
      <div className="prb-header">
        <h2 className="prb-title">
          Zur Freigabe eingereicht
          <span className="prb-badge">{items.length}</span>
        </h2>
        <button className="prb-refresh" onClick={load}>↻ Aktualisieren</button>
      </div>

      <table className="prb-table">
        <thead>
          <tr>
            <th>Dokument</th>
            <th>Autor</th>
            <th>Typ</th>
            <th>Geändert</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item['@id']} className="prb-row">
              <td>
                <a href={item['@id']} target="_blank" rel="noreferrer" className="prb-link">
                  {item.title}
                </a>
              </td>
              <td>{item.Creator ?? '–'}</td>
              <td>{item.portal_type ?? '–'}</td>
              <td>{item.modified ? new Date(item.modified).toLocaleDateString('de-DE') : '–'}</td>
              <td className="prb-actions">
                <button
                  className="prb-btn prb-btn--publish"
                  onClick={() => handleAction(item, 'publish')}
                  disabled={busy === item['@id']}
                >
                  {busy === item['@id'] ? '…' : '✓ Freigeben'}
                </button>
                <button
                  className="prb-btn prb-btn--reject"
                  onClick={() => handleAction(item, 'reject')}
                  disabled={busy === item['@id']}
                >
                  {busy === item['@id'] ? '…' : '✕ Ablehnen'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
